import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BG1 from "./../../../assets/img/giftshop/landing/bg-1.png";
import RightArrow from "../../../compoment/icon/RightArrow";
import {
  CaricatureHobbyBackgroundImage,
  backgroundImageUrl,
  compositeCharactersOnBackground,
  getActiveCaricatureHobbyBackgroundImages,
  isTransparentHobbyBackgroundName,
  persistFavouriteTransparencyFlag,
} from "../../../services/caricatureHobbyService";
import {
  finalizeOrderWithGeneratedArt,
  persistLatestChosenArtUrl,
} from "../../../utils/finalizeOrderWithGeneratedArt";
import Loader from "../../../compoment/ui/Loader";
import type { RootState } from "../../../redux/store";
import { setProcessedImageUrl } from "../../../redux/slices/uploadFlowSlice";
import {
  readSelectedAiSelfieArtFromStorage,
  shouldUsePetCaricatureBackgroundFlow,
} from "../../../services/stylePromptResolver";
import { resolveStationRouteSlug } from "../../../utils/stationFlowStorage";
import StationThemeLayout from "../../../layouts/StationThemeLayout";
import SignOutConfirmModal from "../../../modal/SignOutConfirmModal";

const SELECTED_BG_STORAGE_KEY = "selectedCaricatureHobbyBackground";
const BACKGROUND_SHIMMER_COUNT = 6;

const HobbyBackgroundImageTile = ({
  src,
  alt,
  selected,
  onSelect,
  title,
  overlayLabel,
  showTransparencyCheckerboard = false,
}: {
  src: string;
  alt: string;
  selected: boolean;
  onSelect: () => void;
  title?: string;
  overlayLabel?: string;
  showTransparencyCheckerboard?: boolean;
}) => {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const hasSrc = Boolean(src && src.trim());

  useEffect(() => {
    setLoaded(false);
    if (!hasSrc) {
      setLoaded(true);
      return;
    }
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src, hasSrc]);

  return (
    <div
      className={`bgItem hobbyBackgroundSlide ${selected ? "selected" : ""}${
        showTransparencyCheckerboard ? " hobbyBackgroundSlide--transparent" : ""
      }`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      title={title}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <span
        className={`hobbyBackgroundImgWrap${loaded ? " is-loaded" : ""}${
          showTransparencyCheckerboard ? " hobbyBackgroundImgWrap--checkerboard" : ""
        }`}
      >
        {!loaded && <span className="hobbyBackgroundShimmer" aria-hidden="true" />}
        {hasSrc ? (
          <img
            ref={imgRef}
            src={src}
            alt={alt}
            onLoad={() => setLoaded(true)}
            onError={(e) => {
              const el = e.currentTarget;
              if (showTransparencyCheckerboard) {
                el.style.display = "none";
                setLoaded(true);
                return;
              }
              if (!el.dataset.fallbackApplied) {
                el.dataset.fallbackApplied = "1";
                el.src = BG1;
              }
              setLoaded(true);
            }}
          />
        ) : null}
        {overlayLabel ? (
          <span className="hobbyBackgroundOverlayLabel">{overlayLabel}</span>
        ) : null}
      </span>
    </div>
  );
};

const SelectArtBackground = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug: slugParam } = useParams<{ slug: string }>();
  const dispatch = useDispatch();
  const { processedImageUrl } = useSelector((s: RootState) => s.uploadFlow);

  const [backgrounds, setBackgrounds] = useState<CaricatureHobbyBackgroundImage[]>([]);
  const [backgroundsLoading, setBackgroundsLoading] = useState(true);
  const [backgroundsError, setBackgroundsError] = useState<string | null>(null);
  const [artImageUrl, setArtImageUrl] = useState("");
  /** `null` = transparent / no background */
  const [selectedBackgroundId, setSelectedBackgroundId] = useState<string | null>(null);
  const [isCompositing, setIsCompositing] = useState(false);
  const [showBackConfirmModal, setShowBackConfirmModal] = useState(false);
  const [showProceedConfirmModal, setShowProceedConfirmModal] = useState(false);
  const backgroundSliderRef = useRef<HTMLDivElement>(null);

  const stationData = useMemo(() => {
    const fromNav = (location.state as { stationData?: Record<string, unknown> } | null)?.stationData;
    if (fromNav && typeof fromNav === "object") {
      return fromNav;
    }
    try {
      const selectedRaw = localStorage.getItem("selectedStation");
      const fromSelected = selectedRaw ? JSON.parse(selectedRaw) : null;
      const fromStorage = JSON.parse(localStorage.getItem("stationData") || "{}");
      return fromSelected || fromStorage || {};
    } catch {
      return {};
    }
  }, [location.state]);

  const selectedArt = useMemo(() => readSelectedAiSelfieArtFromStorage(), []);

  const routeSlug = useMemo(
    () =>
      resolveStationRouteSlug(
        stationData as { type?: string; name?: string },
        slugParam,
        selectedArt
      ) ||
      slugParam ||
      "pet-caricature",
    [stationData, slugParam, selectedArt]
  );

  const isPetFlow = shouldUsePetCaricatureBackgroundFlow(
    selectedArt,
    stationData as { type?: string; name?: string }
  );

  // Guard: other stations must never stay on this pet-only screen.
  useEffect(() => {
    if (isPetFlow) return;
    navigate(`/${routeSlug}/print-frame-selection`, {
      replace: true,
      state: { stationData },
    });
  }, [isPetFlow, navigate, routeSlug, stationData]);

  useEffect(() => {
    const fromState = (location.state as { snapshot?: string; artUrl?: string } | null)?.snapshot
      || (location.state as { artUrl?: string } | null)?.artUrl;
    if (typeof fromState === "string" && fromState.trim()) {
      setArtImageUrl(fromState.trim());
      return;
    }
    if (typeof processedImageUrl === "string" && processedImageUrl.trim()) {
      setArtImageUrl(processedImageUrl.trim());
      return;
    }
    try {
      const fromFavourite = localStorage.getItem("favouriteImg")?.trim() || "";
      setArtImageUrl(fromFavourite);
    } catch {
      setArtImageUrl("");
    }
  }, [location.state, processedImageUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBackgroundsLoading(true);
      setBackgroundsError(null);
      const res = await getActiveCaricatureHobbyBackgroundImages();
      if (cancelled) return;
      if (!res.success && res.data.length === 0) {
        setBackgroundsError(res.error || "Could not load backgrounds");
        setBackgrounds([]);
      } else {
        setBackgrounds(res.data);
        // Default select display index 0: first transparent / No Background tile.
        const firstTransparent = res.data.find((bgItem) =>
          isTransparentHobbyBackgroundName(bgItem.name)
        );
        setSelectedBackgroundId(firstTransparent?.id ?? res.data[0]?.id ?? null);
      }
      setBackgroundsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const proceed = async () => {
    if (isCompositing) return;

    const characterUrl = (artImageUrl || "").trim();
    if (!characterUrl) {
      setBackgroundsError("No generated image was found. Please go back and try again.");
      return;
    }

    const bg =
      selectedBackgroundId !== null
        ? backgrounds.find((b) => b.id === selectedBackgroundId)
        : undefined;
    // Pet "Plain White" (null) is the no-background / transparent option.
    const choseTransparent =
      selectedBackgroundId === null || isTransparentHobbyBackgroundName(bg?.name);
    const bgUrl = choseTransparent ? "" : bg ? backgroundImageUrl(bg) : "";

    setIsCompositing(true);
    setBackgroundsError(null);
    const compositeRes = await compositeCharactersOnBackground({
      characterUrls: [characterUrl],
      backgroundUrl: bgUrl,
      backgroundName: choseTransparent ? "Transparent Background" : bg?.name,
      isTransparent: choseTransparent,
    });
    setIsCompositing(false);

    if (!compositeRes.success || !compositeRes.imageUrl) {
      setBackgroundsError(compositeRes.error || "Failed to apply background.");
      return;
    }

    const finalImage = compositeRes.imageUrl;
    persistFavouriteTransparencyFlag(choseTransparent);
    persistLatestChosenArtUrl(finalImage);
    dispatch(setProcessedImageUrl(finalImage));

    const orderResult = await finalizeOrderWithGeneratedArt(finalImage);
    if (!orderResult.success && !orderResult.skipped) {
      setBackgroundsError(orderResult.error || "Failed to attach your artwork to the order.");
      return;
    }

    if (selectedBackgroundId === null) {
      localStorage.removeItem(SELECTED_BG_STORAGE_KEY);
    } else if (bg) {
      try {
        localStorage.setItem(
          SELECTED_BG_STORAGE_KEY,
          JSON.stringify({
            id: bg.id,
            name: bg.name,
            image_url: backgroundImageUrl(bg),
          })
        );
      } catch {
        /* ignore */
      }
    }

    // Match hobby order: background first, then save-favorite, then print.
    navigate(`/${routeSlug}/save-favorite`, {
      state: {
        selectedHobbyBackground: bg ?? null,
        selectedHobbyGeneratedImage: finalImage,
        hobbyCharacterFavouriteUrls: [characterUrl],
        isTransparentHobbyBackground: choseTransparent,
        stationData,
        selectedArt,
      },
    });
  };

  const handleProceedClick = () => {
    if (isCompositing) return;
    setShowProceedConfirmModal(true);
  };

  const handleProceedConfirm = () => {
    setShowProceedConfirmModal(false);
    void proceed();
  };

  if (!isPetFlow) {
    return null;
  }

  return (
    <>
      {isCompositing && <Loader />}
      <StationThemeLayout
        className="personHobbyCreatedSection"
        station={stationData as { backgroundImageUrl?: string; type?: string; name?: string }}
        showStationLogo
        onBack={() => setShowBackConfirmModal(true)}
      >
        <div className="personHobbyCreatedSplit">
          <div>
            <aside className="personHobbyCreatedPreview shadow-sm" aria-label="Pet caricature preview">
              <div className="personHobbyCreatedPreviewInner">
                <img
                  src={artImageUrl || ""}
                  alt="Generated pet caricature"
                  className="hobbyImg"
                />
                {!artImageUrl && (
                  <div className="small text-warning mt-2 text-center px-2">
                    No generated image was found. You can still continue after going back.
                  </div>
                )}
              </div>
            </aside>

            <div className="personHobbyCreatedScroll">
              <div className="selectBrand">
                <div className="desktopfFirstSection">
                  <div className="heading">Pet Caricature Ready</div>
                  <div className="description">
                    Choose a background, then proceed.
                  </div>
                </div>

                <div className="personHobbyCreatedControls">
                  <div className="changeBGTxt">Choose Background</div>
                  {backgroundsError ? (
                    <div className="small text-danger mb-2">{backgroundsError}</div>
                  ) : null}
                  <div className="hobbyBackgroundSliderRow mb-3">
                    <div className="hobbyBackgroundSlider row g-3" ref={backgroundSliderRef}>
                      {backgroundsLoading ? (
                        Array.from({ length: BACKGROUND_SHIMMER_COUNT }).map((_, index) => (
                          <div key={`bg-shimmer-${index}`} className="col-6">
                            <div
                              className="bgItem hobbyBackgroundSlide hobbyBackgroundSlideShimmer"
                              aria-hidden="true"
                            >
                              <span className="hobbyBackgroundShimmer" />
                            </div>
                          </div>
                        ))
                      ) : (
                        <>
                          {(() => {
                            const transparentBackgrounds = backgrounds.filter((bgItem) =>
                              isTransparentHobbyBackgroundName(bgItem.name)
                            );
                            const imageBackgrounds = backgrounds.filter(
                              (bgItem) => !isTransparentHobbyBackgroundName(bgItem.name)
                            );

                            const renderBackgroundTile = (
                              bgItem: CaricatureHobbyBackgroundImage,
                              isTransparentBackground: boolean
                            ) => {
                              // Transparent tile uses checkerboard only — admin sample images
                              // are often opaque white and hide true transparency.
                              const src = isTransparentBackground
                                ? ""
                                : backgroundImageUrl(bgItem) || BG1;

                              return (
                                <div key={bgItem.id} className="col-6">
                                  <HobbyBackgroundImageTile
                                    src={src}
                                    alt={
                                      isTransparentBackground
                                        ? "No Background"
                                        : "Background"
                                    }
                                    selected={selectedBackgroundId === bgItem.id}
                                    onSelect={() => setSelectedBackgroundId(bgItem.id)}
                                    showTransparencyCheckerboard={isTransparentBackground}
                                    overlayLabel={
                                      isTransparentBackground
                                        ? "No Background"
                                        : undefined
                                    }
                                  />
                                </div>
                              );
                            };

                            return (
                              <>
                                {transparentBackgrounds.map((bgItem) =>
                                  renderBackgroundTile(bgItem, true)
                                )}
                                {/* Always show Plain White right after transparent / No Background. */}
                                <div className="col-6">
                                  <div
                                    className={`bgItem hobbyBackgroundSlide ${
                                      selectedBackgroundId === null ? "selected" : ""
                                    }`}
                                    onClick={() => setSelectedBackgroundId(null)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setSelectedBackgroundId(null);
                                      }
                                    }}
                                  >
                                    <div className="noBGSection shadow-sm">Plain White Background</div>
                                  </div>
                                </div>
                                {imageBackgrounds.map((bgItem) =>
                                  renderBackgroundTile(bgItem, false)
                                )}
                              </>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </div>

                  <button
                    className="proceedBTN mt-4"
                    onClick={handleProceedClick}
                    disabled={isCompositing || !artImageUrl}
                  >
                    Proceed
                    <span>
                      {" "}
                      <RightArrow />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </StationThemeLayout>
      {showBackConfirmModal && (
        <SignOutConfirmModal
          show={showBackConfirmModal}
          onHide={() => setShowBackConfirmModal(false)}
          onConfirm={() => {
            setShowBackConfirmModal(false);
            navigate("/");
          }}
          message="Are you sure you want to go back to the home screen?"
          cancelLabel="No"
          confirmLabel="Yes"
        />
      )}
      {showProceedConfirmModal && (
        <SignOutConfirmModal
          show={showProceedConfirmModal}
          onHide={() => setShowProceedConfirmModal(false)}
          onConfirm={handleProceedConfirm}
          message="Are you sure you want to proceed with this background?"
          cancelLabel="No"
          confirmLabel="Yes"
        />
      )}
    </>
  );
};

export default SelectArtBackground;
